import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { SelaChatCitation, SelaProjectDetail } from '../../../services/selaApi';
import { chatMarkdownComponents, formatChatContent } from '../../../utils/chatMarkdown';
import {
  EvidenceStrip,
  linkCitationMarkers,
  normalizeDisplayCitations,
} from '../evidence/EvidenceStrip';
import { useAskSela, type AskSelaFocus, type AskSelaSourcePage } from './AskSelaContext';
import { useAskSelaChat, type AskSelaTurn } from './useAskSelaChat';

const BASE_QUESTIONS = [
  'What should I verify before making an offer?',
  'What is the biggest unresolved risk?',
  'Which development path is safest?',
  'What should I do next?',
];

const NEAR_BOTTOM_PX = 120;

type AskSelaPanelMode = 'drawer' | 'full';

const PAGE_QUESTIONS: Record<AskSelaSourcePage, string[]> = {
  overview: [
    'What changed since the last project update?',
    'What should I focus on next?',
    'What is the current decision posture?',
    'Which risk deserves attention first?',
  ],
  insights: [
    'Why is this the recommendation?',
    'What would make this parcel a no-go?',
    'Which path is fastest to permit?',
    'What should I verify before making an offer?',
  ],
  roadmap: [
    'What should I do next?',
    'Can I skip this requirement?',
    'What document resolves this?',
    'Who should I contact?',
  ],
  people: [
    'Who is blocking progress?',
    'Draft a follow-up email',
    'What should I ask this person?',
    'Which role do I need next?',
  ],
  documents: [
    'What changed after this upload?',
    'Which requirements does this document resolve?',
    'What is still missing?',
    'Does this change the decision brief?',
  ],
  graph: [
    'Explain this fact',
    'What source supports this?',
    'What development paths does this affect?',
    'What should be verified next?',
  ],
  changelog: [
    'What was last updated?',
    'What changed recently?',
    'Which update matters most?',
    'What should I follow up on?',
  ],
};

const CONTEXT_QUESTIONS: Partial<Record<AskSelaFocus['type'], string[]>> = {
  recommendation: ['Ask why this is Investigate', 'What would make this Pursue?', 'What should I verify first?'],
  developmentPath: ['Why is this path conditional?', 'What would make this path stronger?', 'Compare ADU vs DADU for fastest permit path'],
  risk: ['How do I resolve this risk?', 'Who should I talk to?', 'What document would reduce uncertainty?'],
  missingFact: ['What document resolves this?', 'Who should I ask?', 'Why does this matter?'],
  requirement: ['What document resolves this?', 'Who should I contact?', 'What should I ask the surveyor?', 'Can this be skipped?'],
  followUp: ['Draft a follow-up email', 'What should I ask next?', 'Does this block the roadmap?', 'What should I do if they do not respond?'],
  roleNeed: ['What should I ask this expert?', 'Why do I need this role?', 'Can this wait?', 'What should they verify?'],
  person: ['What are we waiting on from this person?', 'Draft a follow-up email', 'What should I ask next?'],
  document: ['What did this document change?', 'Which requirements does this resolve?', 'What is still missing?', 'Does this affect DADU feasibility?'],
  graphNode: ['Explain this fact', 'What evidence supports this?', 'What does this mean for ADU/DADU?', 'What should be verified next?'],
  evidence: ['What does this source prove?', 'What remains uncertain?', 'Which decision does this affect?'],
  changelogEntry: ['What changed here?', 'Does this affect the roadmap?', 'What should I do next?'],
};

function sourcePageFromPath(pathname: string): AskSelaSourcePage {
  if (pathname.includes('/insights')) return 'insights';
  if (pathname.includes('/timeline') || pathname.includes('/roadmap') || pathname.includes('/gates')) return 'roadmap';
  if (pathname.includes('/people')) return 'people';
  if (pathname.includes('/documents')) return 'documents';
  if (pathname.includes('/graph')) return 'graph';
  if (pathname.includes('/changelog')) return 'changelog';
  return 'overview';
}

function focusLabel(focus: AskSelaFocus): string {
  if (focus.type === 'project') return 'Project-wide';
  const prefix: Partial<Record<AskSelaFocus['type'], string>> = {
    decisionBrief: 'Decision Brief',
    recommendation: 'Recommendation',
    developmentPath: 'Path',
    risk: 'Risk',
    missingFact: 'Missing fact',
    nextAction: 'Next action',
    requirement: 'Requirement',
    person: 'Person',
    roleNeed: 'Needed role',
    followUp: 'Follow-up',
    document: 'Document',
    graphNode: 'Fact',
    evidence: 'Evidence',
    changelogEntry: 'Change',
  };
  return `${prefix[focus.type] || 'Context'}: ${focus.label}`;
}

function suggestedQuestions(focus: AskSelaFocus, page: AskSelaSourcePage): string[] {
  const contextQuestions = CONTEXT_QUESTIONS[focus.type];
  const sourceQuestions = PAGE_QUESTIONS[focus.sourcePage || page] || BASE_QUESTIONS;
  return Array.from(new Set([...(contextQuestions || []), ...sourceQuestions, ...BASE_QUESTIONS])).slice(0, 5);
}

function suggestedActions(focus: AskSelaFocus): string[] {
  switch (focus.type) {
    case 'requirement':
      return ['Add note to action', 'Mark needs review', 'Attach document'];
    case 'risk':
      return ['Create roadmap action', 'Add follow-up', 'Mark needs review'];
    case 'followUp':
      return ['Copy draft', 'Save as draft text', 'Mark sent manually'];
    case 'roleNeed':
      return ['Create needed role', 'Add follow-up'];
    case 'document':
      return ['Attach document to action', 'Add missing fact'];
    case 'missingFact':
      return ['Create roadmap action', 'Add missing fact'];
    default:
      return [];
  }
}

function isNearBottom(container: HTMLDivElement): boolean {
  return container.scrollHeight - container.scrollTop - container.clientHeight <= NEAR_BOTTOM_PX;
}

export function AskSelaDrawer({
  project,
  intelKickoffInProgress,
}: {
  project: SelaProjectDetail;
  intelKickoffInProgress?: boolean;
}) {
  const { isOpen, focus, openAskSela, closeAskSela, clearAskSelaFocus, pendingDraft, consumePendingDraft } = useAskSela();
  const location = useLocation();
  const page = sourcePageFromPath(location.pathname);
  const chat = useAskSelaChat(project.id, Boolean(intelKickoffInProgress));
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [panelMode, setPanelMode] = useState<AskSelaPanelMode>('drawer');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const shouldAutoScrollRef = useRef(true);
  const previousTurnCountRef = useRef(0);
  const quickQuestions = useMemo(() => suggestedQuestions(focus, page), [focus, page]);
  const actions = useMemo(() => suggestedActions(focus), [focus]);
  const isFullPage = panelMode === 'full';

  useEffect(() => {
    if (!pendingDraft) return;
    setDraft(consumePendingDraft());
  }, [consumePendingDraft, pendingDraft]);

  useEffect(() => {
    if (!isOpen) return;
    shouldAutoScrollRef.current = true;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !scrollRef.current) return;
    const hasNewTurn = chat.turns.length > previousTurnCountRef.current;
    if (hasNewTurn) {
      shouldAutoScrollRef.current = true;
    }
    previousTurnCountRef.current = chat.turns.length;
    if (!shouldAutoScrollRef.current && !hasNewTurn) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chat.turns.length, chat.streaming, isOpen, panelMode]);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    shouldAutoScrollRef.current = isNearBottom(container);
  }, []);

  const submit = async (question: string) => {
    const trimmed = question.trim();
    if (!trimmed) return;
    shouldAutoScrollRef.current = true;
    await chat.submitQuestion(trimmed, focus);
    setDraft('');
  };

  const collapse = () => {
    setPanelMode('drawer');
    closeAskSela();
  };

  const processingDocument = focus.type === 'document' && focus.metadata?.processingStatus && focus.metadata.processingStatus !== 'extracted';

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => openAskSela(undefined)}
        className="fixed bottom-5 right-5 z-40 rounded-full border border-[#1f3f91]/20 bg-[#0f172a] px-4 py-3 text-sm font-bold text-white shadow-2xl shadow-slate-900/20 hover:bg-[#1e4474] md:bottom-6 md:right-6"
      >
        Ask Sela
      </button>
    );
  }

  return (
    <div
      className={[
        'fixed z-50 flex flex-col border border-[#dbe4f0] bg-white',
        isFullPage
          ? 'inset-x-0 bottom-0 top-0 md:left-[220px] md:right-0 md:top-14 md:rounded-none md:border-y-0 md:border-r-0'
          : 'inset-x-0 bottom-0 max-h-[92vh] rounded-t-3xl shadow-2xl md:inset-y-14 md:left-auto md:right-0 md:max-h-none md:w-[420px] md:rounded-none md:border-y-0 md:border-r-0',
      ].join(' ')}
    >
      <header className="shrink-0 border-b border-[#e7e9ee] bg-[#0f172a] px-3 py-2 text-white">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-100">Ask Sela</p>
              <h2 className="text-sm font-bold">Project assistant</h2>
            </div>
            <span className="hidden max-w-[360px] truncate rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#0f172a] sm:inline">
              {focusLabel(focus)}
            </span>
          </div>
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => setPanelMode(isFullPage ? 'drawer' : 'full')}
              className="rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-slate-100 hover:bg-white/10"
            >
              {isFullPage ? 'Back to drawer' : 'Full page'}
            </button>
            <button
              type="button"
              onClick={collapse}
              className="rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-slate-100 hover:bg-white/10"
            >
              Collapse
            </button>
          </div>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 sm:hidden">
          <span className="max-w-full truncate rounded-full bg-white px-2.5 py-0.5 text-xs font-semibold text-[#0f172a]">
            {focusLabel(focus)}
          </span>
          {focus.type !== 'project' ? (
            <button
              type="button"
              onClick={clearAskSelaFocus}
              className="rounded-full border border-white/20 px-2.5 py-0.5 text-xs font-semibold text-slate-100 hover:bg-white/10"
            >
              Clear context
            </button>
          ) : null}
        </div>
        {focus.type !== 'project' ? (
          <button
            type="button"
            onClick={clearAskSelaFocus}
            className="mt-1 hidden rounded-full border border-white/20 px-2.5 py-0.5 text-xs font-semibold text-slate-100 hover:bg-white/10 sm:inline-flex"
          >
            Clear context
          </button>
        ) : null}
      </header>

      <div ref={scrollRef} onScroll={handleScroll} className="min-h-0 flex-1 overflow-y-auto bg-[#f7fafd] px-4 py-4">
        {chat.loading ? (
          <div className="rounded-xl border border-[#e1e8f0] bg-white p-4 text-sm text-[#647488]">
            Loading project conversation...
          </div>
        ) : chat.loadError ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{chat.loadError}</div>
        ) : chat.turns.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#cdd5e1] bg-white p-4 text-sm leading-relaxed text-[#647488]">
            Ask Sela a question to start a project-level thread for this parcel.
          </div>
        ) : (
          <div className={isFullPage ? 'mx-auto max-w-4xl space-y-4' : 'space-y-4'}>
            {chat.turns.filter((turn) => turn.kind === 'question').map((turn) => (
              <ConversationTurn
                key={turn.key}
                turn={turn}
                streaming={turn.assistant ? chat.streaming[turn.assistant.messageId] : undefined}
                projectId={project.id}
                compactEvidence={!isFullPage}
              />
            ))}
          </div>
        )}

        {processingDocument ? (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
            This document is still processing. Sela can reason from project context now, and can answer document-specific questions once extraction is complete.
          </div>
        ) : null}
      </div>

      <footer className="shrink-0 border-t border-[#e7e9ee] bg-white px-4 py-3">
        <div className={isFullPage ? 'mx-auto max-w-4xl' : ''}>
        <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
          {quickQuestions.map((question) => (
            <button
              key={question}
              type="button"
              onClick={() => void submit(question)}
              disabled={chat.submitting}
              className="shrink-0 rounded-full border border-[#cdd5e1] bg-white px-3 py-1.5 text-xs font-semibold text-[#1e4474] hover:border-[#3251b7] hover:bg-[#eff2fe] disabled:opacity-50"
            >
              {question}
            </button>
          ))}
        </div>
        {actions.length ? (
          <div className="mb-3 rounded-xl border border-[#e1e8f0] bg-[#f8fafc] p-3">
            <p className="text-[10px] font-bold uppercase tracking-wide text-[#647488]">Suggested actions</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {actions.map((action) => (
                <button
                  key={action}
                  type="button"
                  onClick={() => setNotice(`${action} requires confirmation in the linked workflow.`)}
                  className="rounded-md border border-[#cdd5e1] bg-white px-2.5 py-1.5 text-xs font-semibold text-[#334155] hover:border-[#3251b7]"
                >
                  {action}
                </button>
              ))}
            </div>
            {notice ? <p className="mt-2 text-xs text-[#647488]">{notice}</p> : null}
          </div>
        ) : null}
        {chat.submitError ? (
          <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{chat.submitError}</div>
        ) : null}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit(draft);
          }}
          className="flex gap-2"
        >
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void submit(draft);
              }
            }}
            rows={2}
            placeholder="Ask about this parcel, risk, requirement, document, or next action..."
            className="min-h-[46px] flex-1 resize-none rounded-xl border border-[#cdd5e1] bg-white px-3 py-2 text-sm text-[#0f172a] shadow-sm focus:border-[#3251b7] focus:outline-none focus:ring-2 focus:ring-[#3251b7]/20"
            disabled={chat.submitting}
          />
          <button
            type="submit"
            disabled={chat.submitting || draft.trim().length === 0}
            className="h-[46px] rounded-xl bg-[#3251b7] px-4 text-sm font-bold text-white hover:bg-[#1e4474] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {chat.submitting ? '...' : 'Ask'}
          </button>
        </form>
        <p className="mt-2 text-[11px] text-[#858a9a]">
          {chat.wsConnectionId ? 'Streaming is active; answers appear as Sela works.' : 'Streaming unavailable; answers refresh after Sela finishes.'}
        </p>
        </div>
      </footer>
    </div>
  );
}

function ConversationTurn({
  turn,
  streaming,
  projectId,
  compactEvidence,
}: {
  turn: AskSelaTurn;
  streaming?: { content: string; citations?: SelaChatCitation[] };
  projectId: string;
  compactEvidence: boolean;
}) {
  const answer = streaming?.content || turn.assistant?.content || '';
  const isLoading = !turn.assistant || turn.assistant.status === 'pending';
  const askContext = (turn.user?.agentMetadata?.askContext || turn.assistant?.agentMetadata?.askContext) as AskSelaFocus | undefined;
  const citations = streaming?.citations?.length ? streaming.citations : turn.assistant?.citations || [];
  const { citations: displayCitations, markerMap } = normalizeDisplayCitations(citations);
  const linkedAnswer = turn.assistant
    ? linkCitationMarkers(formatChatContent(answer), displayCitations, turn.assistant.messageId, markerMap)
    : formatChatContent(answer);

  return (
    <article className="space-y-2">
      {turn.user ? (
        <div className="ml-8 rounded-2xl bg-[#3251b7] px-3 py-2 text-sm leading-relaxed text-white shadow-sm">
          {askContext?.label ? (
            <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-blue-100">{focusLabel(askContext)}</p>
          ) : null}
          {turn.user.content}
        </div>
      ) : null}
      <div className="mr-6 rounded-2xl border border-[#e1e8f0] bg-white px-3 py-3 text-sm leading-relaxed text-[#334155] shadow-sm">
        {turn.assistant?.status === 'error' ? (
          <p className="text-red-700">{turn.assistant.errorMessage || 'Ask Sela could not answer this question.'}</p>
        ) : answer ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
            {linkedAnswer}
          </ReactMarkdown>
        ) : isLoading ? (
          <div className="space-y-1 text-[#647488]">
            <p>Reviewing project context...</p>
            <p className="text-xs">Checking evidence and preparing a decision-oriented answer.</p>
          </div>
        ) : null}
        {turn.assistant && citations.length ? (
          <EvidenceStrip
            projectId={projectId}
            citations={citations}
            messageId={turn.assistant.messageId}
            compact={compactEvidence}
          />
        ) : null}
      </div>
    </article>
  );
}
